import { userErrorMessage } from '@wx-weiqi/contracts';
import { useEffect, useState } from 'react';
import type { FoxKifuAiEngineCatalog } from '@wx-weiqi/contracts';
import { ZHIZI_ENGINE_WEIGHTS, zhiziWeightLabel } from '../domain/zhizi-profile';
import type { AdminStore } from '../stores/admin-store';

export function ZhiziWeightSelect({
  admin,
  accountId,
  value,
  disabled,
  onChange,
}: {
  readonly admin: Pick<AdminStore, 'loadZhiziEngineCatalog'>;
  readonly accountId: string;
  readonly value: string;
  readonly disabled: boolean;
  readonly onChange: (value: string) => void;
}) {
  const [catalog, setCatalog] = useState<FoxKifuAiEngineCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    if (!accountId) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    void admin
      .loadZhiziEngineCatalog(accountId, refresh > 0)
      .then((result) => {
        if (active) setCatalog(result);
      })
      .catch(() => {
        if (active) setError('权重目录更新失败，已保留当前列表，请稍后重试。');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [admin, accountId, refresh]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') setRefresh((count) => count + 1);
    }, 5 * 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const options =
    catalog?.weights ?? ZHIZI_ENGINE_WEIGHTS.map((name) => ({ name, description: '' }));
  const missing = value !== '' && !options.some((option) => option.name === value);
  return (
    <div className="teacher-ai-management__weight">
      <label>
        引擎权重
        <select
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        >
          {missing ? <option value={value}>{value}（保留当前选择）</option> : null}
          {options.map((option) => (
            <option key={option.name} value={option.name}>
              {zhiziWeightLabel(option.name)}
              {option.description ? ` · ${option.description}` : ''}
            </option>
          ))}
        </select>
      </label>
      <div className="teacher-ai-management__catalog-actions">
        <small role="status">
          {loading
            ? '正在检查权重目录…'
            : catalog?.checkedAt
              ? `上次成功更新：${new Date(catalog.checkedAt).toLocaleString('zh-CN')}${catalog.serverVersion ? ` · 服务版本 ${catalog.serverVersion}` : ''}`
              : '内置兼容列表 · 尚未获取在线目录'}
        </small>
        <button
          className="secondary-button"
          type="button"
          disabled={disabled || loading || !accountId}
          onClick={() => setRefresh((count) => count + 1)}
        >
          {loading ? '更新中…' : '刷新权重'}
        </button>
      </div>
      {error || catalog?.warning ? (
        <small className="teacher-ai-management__catalog-warning" role="status">
          {userErrorMessage(error ?? catalog?.warning, '权重目录暂时无法更新，请稍后重试。')}
        </small>
      ) : null}
      <small>
        每 5 分钟检查更新。刷新不会改变当前选择；选择新权重后点击保存，下次启动 AI 时生效。
      </small>
    </div>
  );
}
